import {
  formIsApplication,
  formIsMedRec,
  formIsRecordOfDetention,
  isGuestUserSession,
  type ExtendedThalamosUser,
  type FormContextData,
  type WorkItem,
} from "@thalamos/aspire-common";

/**
 * Returns `true` when the user is a guest who was invited to this admission as
 * a doctor — i.e. their team's work item either has `requestType` equal to
 * `"medical-recommendation"`, or has been subsequently changed (e.g. to
 * `"amend"` or `"complete"`) but still points at a medical recommendation form.
 *
 * Guests invited as an AMHP, MHA office or ward team member will return
 * `false`.
 */
export function isGuestDoctorForFormContext(
  user: ExtendedThalamosUser,
  formContext: Pick<FormContextData, "workItems" | "forms">,
): boolean {
  if (!isGuestUserSession(user)) return false;

  const guestTeamId = user.sessionContext?.teamId;

  return formContext.workItems.some((wi) => {
    if (wi.teamId !== guestTeamId) return false;

    // Direct match — the work item still has the original requestType
    if (wi.requestType === "medical-recommendation") return true;

    // After an amend/de-minimis/complete the requestType is overwritten, so
    // fall back to checking whether the work item's linked form is a med rec.
    if (wi.formId) {
      const linkedForm = formContext.forms.find((f) => f.id === wi.formId);
      if (linkedForm && formIsMedRec(linkedForm)) return true;
    }

    return false;
  });
}

/**
 * Returns `true` when the work item is recognisably a workflow other than
 * making a medical recommendation — making the application, or recording the
 * detention.
 *
 * Recording "I will not make a medical recommendation" completes the work item
 * it is written to, so writing it to one of these ends that workflow instead
 * and tells its participants a decision nobody took. `requestType` alone cannot
 * answer this, because asking a team to complete, amend or redo a named form
 * overwrites it; the form the item points at is what survives.
 *
 * Deliberately one-way: work it cannot place counts as the doctor's own, so an
 * unfamiliar request type refuses nothing that works today. Inverting this into
 * a list of what is allowed would block a doctor from recording a decision they
 * have made, which is the worse failure of the two.
 */
export function workItemIsAnotherWorkflow(
  workItem: Pick<WorkItem, "requestType" | "formId">,
  forms: FormContextData["forms"],
): boolean {
  if (
    workItem.requestType === "application" ||
    workItem.requestType === "record-of-detention" ||
    workItem.requestType === "finalise"
  ) {
    return true;
  }

  const linkedForm = workItem.formId
    ? forms.find((f) => f.id === workItem.formId)
    : undefined;

  return (
    linkedForm !== undefined &&
    (formIsApplication(linkedForm) || formIsRecordOfDetention(linkedForm))
  );
}

/**
 * Returns `true` when the work item is a pending amend assigned to a specific
 * user who isn't the current user. Used to suppress the amend flow for
 * teammates on the same team as the assigned signatory — without them, both
 * doctors on a joint med rec amend would see the same pending work item.
 */
export function isAmendForOtherUser(
  wi:
    | {
        status: WorkItem["status"];
        requestType?: WorkItem["requestType"];
        assignedUserId?: string | null;
      }
    | null
    | undefined,
  currentUserId: string,
): boolean {
  return (
    !!wi &&
    wi.status === "pending" &&
    wi.requestType === "amend" &&
    !!wi.assignedUserId &&
    wi.assignedUserId !== currentUserId
  );
}

export function hasNoActiveWorkItemOrRejectedOrCancelled(
  formContext: FormContextData,
) {
  return (
    !formContext.activeTeamworkWorkItem ||
    ["rejected", "cancelled"].includes(
      formContext.activeTeamworkWorkItem.status,
    )
  );
}

export function hasNoActiveWorkItemOrRejectedCancelledOrCompleted(
  formContext: Pick<FormContextData, "activeTeamworkWorkItem">,
) {
  return (
    !formContext.activeTeamworkWorkItem ||
    ["rejected", "cancelled", "completed"].includes(
      formContext.activeTeamworkWorkItem.status,
    )
  );
}
